import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { 
  PencilIcon, 
  TrashIcon, 
  EyeIcon, 
  DocumentDuplicateIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { EmptyState } from '../ui/EmptyState';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { handleAccessControlErrorInComponent } from '@/services/accessControlService';
import { StatusToggle } from './StatusToggle';
import { Pagination } from './Pagination';

interface FlexibleCRUDTableProps {
  entity: any;
  service: any;
  onView?: (item: any) => void;
  onEdit?: (item: any) => void;
  onDelete?: (item: any) => void;
  onCopy?: (item: any) => void;
  onCreate?: () => void;
  onRowClick?: (item: any) => void;
  onToggleStatus?: (id: string, isActive: boolean) => Promise<void>;
  canEdit?: (item: any) => boolean; // Function to determine if item can be edited
  canCopy?: (item: any) => boolean; // Function to determine if item can be copied / new-versioned
  canDelete?: (item: any) => boolean; // Function to determine if item can be deleted
  customActions?: Array<{
    label: string | ((item: any) => string);
    icon: React.ComponentType<any> | ((item: any) => React.ComponentType<any>);
    onClick: (item: any) => void | Promise<void>;
    variant?: 'primary' | 'secondary' | 'danger' | ((item: any) => 'primary' | 'secondary' | 'danger');
    /** When set, action is shown only when this returns true. */
    visible?: (item: any) => boolean;
  }>;
  viewActionIcon?: React.ComponentType<any>; // Custom icon for view action
  viewActionLabel?: string; // Custom label for view action
  editActionIcon?: React.ComponentType<any>; // Custom icon for edit action (e.g. Cog6ToothIcon for project settings)
  editActionLabel?: string; // Custom label for edit action
  searchPlaceholder?: string;
  showCreateButton?: boolean;
  showSearch?: boolean;
  showPagination?: boolean;
  pageSize?: number;
  className?: string;
  onOperationComplete?: () => void; // Callback after successful operations
  enableRowSelection?: boolean; // Enable row-level selection
  onSelectionChange?: (selectedIds: string[]) => void; // Callback when selection changes
  externalCreatedItem?: any; // Newly created item from external modal flow
  /** Use shared ConfirmDialog for delete confirmation (enables "Don't ask again" for bulk delete on same page) */
  useConfirmDialog?: boolean;
  /** When true, skip showing delete confirmation (used with "Don't ask again") */
  skipDeleteConfirmation?: boolean;
  /** Called when user confirms delete with "Don't ask again" checked */
  onDeleteDontAskAgain?: () => void;
  /** Extra query params merged into getAll() (e.g. roadmap_id for filtering by roadmap) */
  extraQueryParams?: Record<string, string | undefined>;
  /**
   * Controls rendered immediately after Search in the table toolbar
   * (e.g. on-demand Filters icon — see On-Demand List Filters principle).
   */
  toolbarAfterSearch?: React.ReactNode;
  /** Panel rendered under the toolbar row (e.g. expanded filter fields). */
  belowToolbar?: React.ReactNode;
}

export const FlexibleCRUDTable: React.FC<FlexibleCRUDTableProps> = ({
  entity,
  service,
  onView,
  onEdit,
  onDelete,
  onCopy,
  onCreate,
  onRowClick,
  onToggleStatus,
  canEdit,
  canCopy,
  canDelete,
  customActions = [],
  viewActionIcon,
  viewActionLabel,
  editActionIcon,
  editActionLabel,
  searchPlaceholder = 'Search...',
  showCreateButton = true,
  showSearch = true,
  showPagination: _showPagination = true,
  pageSize: _pageSize = 10,
  className = '',
  onOperationComplete,
  enableRowSelection = false,
  onSelectionChange,
  externalCreatedItem,
  useConfirmDialog = false,
  skipDeleteConfirmation = false,
  onDeleteDontAskAgain,
  extraQueryParams,
  toolbarAfterSearch,
  belowToolbar
}) => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [wasSearchFocused, setWasSearchFocused] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<any>(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [hoveredInteractions, setHoveredInteractions] = useState<any[]>([]);
  const [loadingInteractions, setLoadingInteractions] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{item: any; confirmed: boolean} | null>(null);
  
  // Sorting state - use entity.defaultSort when available, else default to name A-Z
  const defaultSort = entity?.defaultSort;
  const [sortColumn, setSortColumn] = useState<string>(defaultSort?.field || 'name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultSort?.direction || 'asc');
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: _pageSize
  });
  const { t } = useLanguage();
  const handleError = handleAccessControlErrorInComponent;
  

  const idField = entity.idField || 'id';
  const nameField = entity.nameField || 'name';
  const actionsColumnWidth = entity.actionsColumnWidth ?? '20%';
  const hoverTooltipEnabled = entity.disableHoverTooltip !== true;

  const columnsToShow =
    entity.tableColumns ||
    (entity.displayFields && entity.fields
      ? entity.displayFields
          .map((fieldKey: string) => entity.fields.find((f: any) => f.key === fieldKey))
          .filter(Boolean)
      : entity.fields?.filter((f: any) => !f.tableHidden));

  // Component methods

  // Debounce search term for server-side search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to page 1 when search term changes
      if (searchTerm !== debouncedSearchTerm) {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  // Track if search input was focused before data changes
  const handleSearchFocus = useCallback(() => {
    setWasSearchFocused(true);
  }, []);

  const handleSearchBlur = useCallback(() => {
    setWasSearchFocused(false);
  }, []);

  // Load data on mount and when pagination, search, sorting, or extra params change
  useEffect(() => {
    loadData();
  }, [pagination.currentPage, pagination.itemsPerPage, debouncedSearchTerm, sortColumn, sortDirection, extraQueryParams]);

  // Restore focus after data updates
  useEffect(() => {
    if (wasSearchFocused && searchInputRef.current && !loading) {
      const cursorPosition = searchTerm.length;
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
    }
  }, [filteredData, loading, wasSearchFocused, searchTerm]);


  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPagination(prev => ({ 
      ...prev, 
      itemsPerPage: size, 
      currentPage: 1 // Reset to first page when changing page size
    }));
  }, []);

  // Handle column sorting
  const handleSort = useCallback((columnKey: string) => {
    if (sortColumn === columnKey) {
      // Toggle direction if clicking the same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
    // Reset to first page when sorting changes
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [sortColumn]);

  const loadData = async (overridePage?: number) => {
    try {
      setLoading(true);
      
      // Server-side pagination, search, and sorting
      const params: Record<string, unknown> = {
        page: overridePage !== undefined ? overridePage : pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: debouncedSearchTerm || undefined,
        sortBy: sortColumn,
        sortOrder: sortDirection
      };
      if (extraQueryParams) {
        Object.entries(extraQueryParams).forEach(([key, value]) => {
          if (value !== undefined && value !== '') params[key] = value;
        });
      }

      const result = await service.getAll(params);
      
      
      
      // Handle unified pagination response format
      let data: any[] = [];
      let paginationData = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNext: false,
        hasPrev: false
      };
      
      if (result && Array.isArray(result)) {
        // Legacy format - no pagination
        data = result;
        paginationData.totalItems = result.length;
        paginationData.totalPages = 1;
      } else if (result && result.data && Array.isArray(result.data)) {
        // Unified format: { data: [...], pagination: { page, limit, total, totalPages, hasNext, hasPrev } }
        data = result.data;
        if (result.pagination) {
          paginationData = {
            currentPage: result.pagination.page || 1,
            totalPages: result.pagination.totalPages || 1,
            totalItems: result.pagination.total || 0,
            itemsPerPage: result.pagination.limit || 10,
            hasNext: result.pagination.hasNext || false,
            hasPrev: result.pagination.hasPrev || false
          };
        }
      }
      
      // Apply client-side sorting (works as fallback if backend doesn't support server-side sorting)
      let sortedData = data;
      if (sortColumn && data.length > 0) {
        // Check if the sort column exists in the data
        const hasSortColumn = data.some(item => Object.prototype.hasOwnProperty.call(item, sortColumn));
        
        if (hasSortColumn) {
          sortedData = [...data].sort((a, b) => {
            const aValue = a[sortColumn];
            const bValue = b[sortColumn];
            
            // Handle null/undefined values
            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
            if (bValue == null) return sortDirection === 'asc' ? 1 : -1;
            
            // Handle dates
            if (sortColumn === 'updated_at' || sortColumn === 'created_at' || sortColumn.includes('_at') || sortColumn.includes('date')) {
              const aDate = new Date(aValue).getTime();
              const bDate = new Date(bValue).getTime();
              if (isNaN(aDate) || isNaN(bDate)) {
                // If date parsing fails, fall back to string comparison
                return sortDirection === 'asc' 
                  ? String(aValue).localeCompare(String(bValue))
                  : String(bValue).localeCompare(String(aValue));
              }
              return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
            }
            
            // Handle strings
            if (typeof aValue === 'string' && typeof bValue === 'string') {
              return sortDirection === 'asc' 
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
            }
            
            // Handle numbers
            if (typeof aValue === 'number' && typeof bValue === 'number') {
              return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            }
            
            // Fallback to string comparison
            const aStr = String(aValue);
            const bStr = String(bValue);
            return sortDirection === 'asc' 
              ? aStr.localeCompare(bStr)
              : bStr.localeCompare(aStr);
          });
        }
      }
      
      setFilteredData(sortedData);
      setPagination(paginationData);
      
    } catch (error: any) {
      console.error('Error loading data:', error);
      // Use intelligent error handling for access control errors
      handleError(error);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const insertItemLocally = (item: any) => {
    setFilteredData(prev => {
      const next = [item, ...prev];
      const seen = new Set<string>();
      return next.filter(row => {
        const rowId = String(row?.[idField] ?? '');
        if (!rowId || seen.has(rowId)) return false;
        seen.add(rowId);
        return true;
      });
    });
    setPagination(prev => ({ ...prev, totalItems: prev.totalItems + 1 }));
  };

  const removeItemLocally = (item: any) => {
    const itemId = item?.[idField];
    let removed = false;
    setFilteredData(prev => {
      const next = prev.filter(row => {
        const keep = row?.[idField] !== itemId;
        if (!keep) removed = true;
        return keep;
      });
      return next;
    });
    if (removed) {
      setPagination(prev => ({ ...prev, totalItems: Math.max(0, prev.totalItems - 1) }));
    }
    return removed;
  };

  useEffect(() => {
    if (!externalCreatedItem || typeof externalCreatedItem !== 'object') {
      return;
    }
    insertItemLocally(externalCreatedItem);
    // Keep UI instant and reconcile in background.
    loadData().catch(() => void 0);
  }, [externalCreatedItem]);

  const handleDelete = async (item: any, skipConfirmation: boolean = false) => {
    if (!onDelete && !entity.deletable) {
      return;
    }
    
    // Prevent multiple simultaneous deletes
    if (deletingId !== null) {
      return;
    }
    
    // Confirmation is now handled via React modal in the button click handler
    // We only get here if user already confirmed in the modal
    if (!skipConfirmation) {
      // This shouldn't happen - confirmation should always be done via modal
      // If we somehow get here without confirmation, don't proceed
      console.warn('Delete attempted without confirmation - this should not happen');
      return;
    }
    
    let previousData: any[] = [];
    let previousPagination = { ...pagination };
    try {
      setDeletingId(item[idField]);
      previousData = [...filteredData];
      previousPagination = { ...pagination };
      removeItemLocally(item);
      
      if (onDelete) {
        // If onDelete is provided, let the parent component handle the deletion
        await onDelete(item);
        toast.success(t('messages.itemDeleted'));
      } else {
        // Direct deletion via service
        await service.delete(item[idField]);
        
        toast.success(t('messages.itemDeleted'));
      }

      // Sync table state with server without blocking instant UI feedback.
      loadData().catch(() => void 0);
      
      onOperationComplete?.();
    } catch (error) {
      // Roll back optimistic delete if API call fails.
      setFilteredData(previousData);
      setPagination(previousPagination);
      handleError(error);
    } finally {
      setDeletingId(null);
    }
  };


  const handleCopy = async (item: any) => {
    if (!onCopy && !entity.copyable) return;
    
    try {
      setCopyingId(item[idField]);
      if (onCopy) {
        await onCopy(item);
      } else {
        // Create a copy by calling the service create method
        const copyData = { ...item };
        
        // Remove system fields that shouldn't be copied
        delete copyData[idField]; // Remove the primary key
        delete copyData.created_at;
        delete copyData.updated_at;
        delete copyData.tenant_id; // Remove tenant_id (will be set by backend)
        
        // Remove computed fields (these are generated by JOINs, not actual table columns)
        // Keep the actual foreign key IDs but remove display names
        delete copyData.category_name;
        delete copyData.register_name;
        delete copyData.risk_title;
        delete copyData.mitigation_title;
        delete copyData.author_name;
        delete copyData.current_owner_name;
        delete copyData.identified_by_name;
        delete copyData.business_hierarchy_name;
        delete copyData.functional_hierarchy_name;
        delete copyData.geographic_hierarchy_name;
        delete copyData.strategy_name;
        delete copyData.vision_name;
        delete copyData.roadmap_name;
        
        // Reset status and ownership for copied risk
        copyData.risk_status = 'IDENTIFIED'; // Reset to initial status
        copyData.current_owner_id = null; // Remove current owner
        copyData.assigned_date = null; // Remove assignment date
        
        // Preserve all other fields including:
        // - escalation_level (Risk Level)
        // - business_hierarchy_id, functional_hierarchy_id, geographic_hierarchy_id
        // - severity, probability, due_date
        // - description, mitigation_plan, etc.
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_');
        const removeEmptyOptionalFields = (requiredFields: string[]) => {
          Object.keys(copyData).forEach((key) => {
            if (requiredFields.includes(key)) return;
            if (copyData[key] === '' || copyData[key] === null) {
              delete copyData[key];
            }
          });
        };

        // Contact copy normalization:
        // - clear empty company_id (backend expects UUID if provided)
        // - keep lead_score within accepted enum values
        // - make email unique for copied contact to avoid duplicate collisions
        if (entity.apiEndpoint?.includes('/crm_contacts')) {
          removeEmptyOptionalFields(['first_name', 'last_name']);

          if (!copyData.company_id) {
            delete copyData.company_id;
          }

          const allowedLeadScores = ['cold', 'warm', 'hot', 'qualified'];
          if (copyData.lead_score && !allowedLeadScores.includes(copyData.lead_score)) {
            delete copyData.lead_score;
          }

          if (typeof copyData.email === 'string' && copyData.email.includes('@')) {
            const [localPart, domainPart] = copyData.email.split('@');
            copyData.email = `${localPart}+copy${timestamp}@${domainPart}`;
          }
        }

        // Company copy normalization:
        // - remove empty optional values so copy isn't stricter than create
        // - map historical size values to current allowed buckets
        if (entity.apiEndpoint?.includes('/crm_companies')) {
          removeEmptyOptionalFields(['name']);

          const sizeMapping: Record<string, string> = {
            startup: '0-10',
            small: '11-100',
            medium: '101-1000',
            large: '1001-10000',
            'mid-market': '10001-50000',
            enterprise: '50000+'
          };

          if (typeof copyData.size_category === 'string' && sizeMapping[copyData.size_category]) {
            copyData.size_category = sizeMapping[copyData.size_category];
          }
        }
        
        // Generate a unique name for the copy
        const nameField = entity.nameField || 'name';
        if (copyData[nameField]) {
          copyData[nameField] = `${copyData[nameField]} (Copy ${timestamp})`;
        }
        
        const createdItem = await service.create(copyData);
        if (createdItem && typeof createdItem === 'object') {
          insertItemLocally(createdItem);
        }
      }
      // Sync with backend in background while keeping optimistic row visible.
      loadData().catch(() => void 0);
      toast.success(t('messages.itemCreated'));
      onOperationComplete?.();
    } catch (error) {
      handleError(error);
    } finally {
      setCopyingId(null);
    }
  };

  // Selection handling functions
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredData.map(item => item[idField]));
      setSelectedIds(allIds);
      onSelectionChange?.(Array.from(allIds));
    } else {
      setSelectedIds(new Set());
      onSelectionChange?.([]);
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    const newSelectedIds = new Set(selectedIds);
    if (checked) {
      newSelectedIds.add(id);
    } else {
      newSelectedIds.delete(id);
    }
    setSelectedIds(newSelectedIds);
    onSelectionChange?.(Array.from(newSelectedIds));
  };

  const handleMouseEnter = async (item: any, event: React.MouseEvent) => {
    setHoveredItem(item);
    setHoverPosition({ x: event.clientX, y: event.clientY });
    
    // Only fetch interactions for CRM contacts, not for users or other entities
    // Check if this is a contact entity by checking entity name or API endpoint
    const isContactEntity = entity?.name === 'contacts' || 
                           entity?.apiEndpoint?.includes('crm_contacts') ||
                           entity?.apiEndpoint?.includes('/crm/contacts');
    
    // Only fetch interactions if:
    // 1. This is a contact entity (not users)
    // 2. The item has first_name, last_name, and id
    // 3. The service has getInteractions method (for CRM contacts)
    if (isContactEntity && item.first_name && item.last_name && item.id) {
      setLoadingInteractions(true);
      try {
        // Check if service has getInteractions method or if we need to use a different approach
        // For contacts, we'll fetch interactions using the CRM service if available
        if (service && typeof service.getInteractions === 'function') {
          const response = await service.getInteractions({ contact_id: item.id, limit: 10 });
          const interactions = response?.data || response || [];
          setHoveredInteractions(interactions);
        } else {
          // Try to fetch using the API directly
          const apiUrl = ((import.meta as any).env && 'VITE_API_URL' in (import.meta as any).env ? (import.meta as any).env.VITE_API_URL : undefined) || 'http://localhost:3000';
          const response = await fetch(`${apiUrl}/api/crm/interactions?contact_id=${item.id}&limit=10`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          if (response.ok) {
            const data = await response.json();
            setHoveredInteractions(data.data || []);
          }
        }
      } catch (error) {
        console.error('Error fetching interactions:', error);
        setHoveredInteractions([]);
      } finally {
        setLoadingInteractions(false);
      }
    } else {
      setHoveredInteractions([]);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
    setHoveredInteractions([]);
    setLoadingInteractions(false);
  };

  const isAllSelected = filteredData.length > 0 && selectedIds.size === filteredData.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < filteredData.length;

  const formatValue = (value: any, type?: string): string => {
    if (value === null || value === undefined) return '-';
    
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(value);
      case 'percentage':
        return `${value}%`;
      case 'number':
        return new Intl.NumberFormat().format(value);
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'datetime':
        return new Date(value).toLocaleString();
      case 'boolean':
        return value ? t('common.yes') : t('common.no');
      default:
        return String(value);
    }
  };

  const toolbar = (showSearch || showCreateButton || toolbarAfterSearch || belowToolbar) && (
    <div className="border-b border-[var(--color-border)]">
      <div className="flex flex-col gap-2 p-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
          {showSearch && (
            <div className="relative w-full sm:w-auto">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[var(--color-secondary-text)]" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-[var(--color-border)] bg-transparent text-[var(--color-surface-text)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] focus:border-transparent"
              />
            </div>
          )}
          {toolbarAfterSearch}
        </div>

        {showCreateButton && onCreate && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCreate();
            }}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] text-sm font-medium rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('common.create')}
          </button>
        )}
      </div>
      {belowToolbar}
    </div>
  );

  if (loading) {
    return (
      <div className={`w-full ${className}`}>
        {toolbar}
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-[var(--color-secondary-text)]">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      {toolbar}

        {/* Empty State */}
        {filteredData.length === 0 ? (
          <EmptyState
            title={entity.name || t('tables.noData')}
            count={0}
            message={t('tables.noResults')}
            action={onCreate && (
              <button
                onClick={onCreate}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                {t('common.create')}
              </button>
            )}
          />
        ) : (
          /* Table */
          <div className="table-wrapper border border-[var(--color-border)] rounded-lg overflow-x-auto overflow-y-visible">
            <table className="responsive-table w-full divide-y divide-[var(--color-border)]" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                {enableRowSelection ? <col style={{ width: '50px' }} /> : null}
                {columnsToShow?.map((column: any) => (
                  <col key={column.key} style={{ width: column.width }} />
                ))}
                <col style={{ width: actionsColumnWidth }} />
              </colgroup>
              <thead className="sticky top-0 z-0 border-b border-[var(--color-border)]">
                <tr>
                  {/* Selection checkbox column */}
                  {enableRowSelection && (
                    <th className="px-3 lg:px-6 py-3 text-left text-sm font-medium text-[var(--color-secondary-text)] uppercase tracking-wider" style={{ width: '50px' }}>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          ref={(input) => {
                            if (input) input.indeterminate = isIndeterminate;
                          }}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="h-4 w-4 text-[var(--color-info)] focus:ring-[var(--color-info)] border-[var(--color-border)] rounded"
                        />
                      </div>
                    </th>
                  )}
                  {columnsToShow?.map((column: any) => {
                      const isSorted = sortColumn === column.key;
                      const isAscending = sortDirection === 'asc';
                      
                      return (
                        <th
                          key={column.key}
                          className="px-3 lg:px-6 py-3 text-left text-sm font-medium text-[var(--color-secondary-text)] uppercase tracking-wider cursor-pointer hover:bg-[var(--color-surface-bg)] transition-colors"
                          style={{ width: column.width }}
                          onClick={() => handleSort(column.key)}
                        >
                          <div className="flex items-center gap-1 whitespace-normal break-words">
                            <span>{t(column.label)}</span>
                            {isSorted && (
                              <span className="flex-shrink-0">
                                {isAscending ? (
                                  <ChevronUpIcon className="w-4 h-4 text-[var(--color-surface-text)]" />
                                ) : (
                                  <ChevronDownIcon className="w-4 h-4 text-[var(--color-surface-text)]" />
                                )}
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  <th className="px-3 lg:px-6 py-3 text-right text-sm font-medium text-[var(--color-secondary-text)] uppercase tracking-wider" style={{ width: actionsColumnWidth }}>
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {filteredData.map((item) => (
                <tr
                  key={item[idField]}
                  className="hover:bg-[var(--color-secondary-bg)] transition-colors cursor-pointer"
                  onClick={(e) => {
                    // Only trigger row click if the click didn't come from a button or action
                    const target = e.target as HTMLElement;
                    const isInteractive =
                      target.closest('button') ||
                      target.closest('[role="button"]') ||
                      target.closest('select');
                    if (!isInteractive) {
                      onRowClick?.(item);
                    }
                  }}
                >
                  {/* Selection checkbox */}
                  {enableRowSelection && (
                    <td className="px-3 lg:px-6 py-4 text-sm text-[var(--color-surface-text)]">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item[idField])}
                          onChange={(e) => handleSelectRow(item[idField], e.target.checked)}
                          className="h-4 w-4 text-[var(--color-info)] focus:ring-[var(--color-info)] border-[var(--color-border)] rounded"
                        />
                      </div>
                    </td>
                  )}
                  {columnsToShow?.map((column: any, columnIndex: number) => {
                     const rawValue = item[column.key];
                     let displayValue: any = rawValue;

                     // Check if column has a custom render function
                     if (column.render) {
                       // Use the column's custom render function
                       displayValue = column.render(rawValue, item);
                     } else {
                       // Find the corresponding field definition for formatting
                       const field = entity.fields?.find((f: any) => f.key === column.key);
                       
                       // If this is a select field with options, try to map value -> translated label
                       if (field?.type === 'select' && Array.isArray(field.options)) {
                         const match = field.options.find((opt: any) => (opt?.value ?? opt) === rawValue);
                         if (match) {
                           const translatedLabel = match.label ? t(match.label) : (match?.value ?? rawValue);
                           displayValue = translatedLabel;
                         }
                       } else if (field?.type === 'boolean') {
                         // Leverage existing formatting for booleans
                         displayValue = formatValue(rawValue, 'boolean');
                       } else if (field?.type === 'custom' && field.render) {
                         // Use custom render function if provided
                         displayValue = field.render(rawValue, item);
                       } else {
                         // Fallback to default formatting
                         displayValue = formatValue(rawValue, field?.type);
                       }
                     }

                     // Check for color coding
                     const field = entity.fields?.find((f: any) => f.key === column.key);
                     const colorClasses = field?.colorCoding?.[rawValue];
                     
                     const isTooltipColumn = hoverTooltipEnabled && columnIndex < 2;
                     const cellWrapClass =
                       column.cellClassName ?? 'max-w-xs lg:max-w-none break-words';
                     
                     return (
                       <td 
                         key={column.key} 
                         className="px-3 lg:px-6 py-4 text-sm text-[var(--color-surface-text)] overflow-hidden" 
                         style={{ width: column.width }}
                         onMouseEnter={isTooltipColumn ? (e) => handleMouseEnter(item, e) : undefined}
                         onMouseLeave={isTooltipColumn ? handleMouseLeave : undefined}
                       >
                         <div className={cellWrapClass}>
                           {column.key === 'status' && onToggleStatus ? (
                             <StatusToggle 
                               value={rawValue} 
                               item={item} 
                               onToggle={async (id, nextActive) => {
                                 await onToggleStatus(id, nextActive);
                                 loadData().catch(() => undefined);
                               }}
                             />
                           ) : React.isValidElement(displayValue) 
                             ? displayValue 
                             : typeof displayValue === 'string' && displayValue.includes('<br>') 
                             ? <div dangerouslySetInnerHTML={{ __html: displayValue }} />
                             : colorClasses ? (
                               <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses}`}>
                                 {displayValue}
                               </span>
                             ) : (
                               <div className="whitespace-normal break-words">{displayValue}</div>
                             )
                           }
                         </div>
                       </td>
                     );
                    })}
                  <td className="px-3 lg:px-6 py-4 text-right text-sm font-medium">
                    <div className="flex flex-wrap items-center justify-end gap-1 min-w-0">
                      {/* View */}
                      {onView && (() => {
                        const ViewIcon = viewActionIcon || EyeIcon;
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              onView(item);
                            }}
                            onMouseDown={(e) => {
                              // Prevent row click from firing
                              e.stopPropagation();
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title={viewActionLabel || t('common.view')}
                            type="button"
                          >
                            <ViewIcon style={{ width: '18px', height: '18px' }} />
                          </button>
                        );
                      })()}
                      
                      {/* Edit */}
                      {onEdit && (canEdit ? canEdit(item) : true) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(item);
                          }}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                          title={editActionLabel || t('common.edit')}
                        >
                          {React.createElement(editActionIcon || PencilIcon, { style: { width: '16px', height: '16px' } })}
                        </button>
                      )}
                      
                      {/* Copy */}
                      {(onCopy || entity.copyable) && (canCopy ? canCopy(item) : true) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(item);
                          }}
                          disabled={copyingId === item[idField]}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-md transition-colors disabled:opacity-50"
                          title={t('common.copy')}
                        >
                          <DocumentDuplicateIcon style={{ width: '16px', height: '16px' }} />
                        </button>
                      )}
                      
                      {/* Delete */}
                      {(onDelete || entity.deletable) && (canDelete ? canDelete(item) : true) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (useConfirmDialog && skipDeleteConfirmation) {
                              handleDelete(item, true);
                            } else {
                              setPendingDelete({ item, confirmed: false });
                            }
                          }}
                          onMouseDown={(e) => {
                            // Prevent row click from firing
                            e.stopPropagation();
                          }}
                          disabled={deletingId === item[idField]}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                          title={t('common.delete')}
                          type="button"
                        >
                          <TrashIcon style={{ width: '16px', height: '16px' }} />
                        </button>
                      )}
                      
                      {/* Custom Actions */}
                      {customActions.map((action, index) => {
                        if (action.visible && !action.visible(item)) return null;
                        const IconComponent = typeof action.icon === 'function' ? (action.icon as (item: any) => React.ComponentType<any>)(item) : action.icon;
                        const label = typeof action.label === 'function' ? (action.label as (item: any) => string)(item) : action.label;
                        const variant = typeof action.variant === 'function' ? (action.variant as (item: any) => 'primary' | 'secondary' | 'danger')(item) : action.variant;
                        
                        return (
                          <button
                            key={index}
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await action.onClick(item);
                                await loadData();
                                onOperationComplete?.();
                              } catch (error) {
                                handleError(error);
                              }
                            }}
                            className={`p-2 rounded-md transition-colors ${
                              variant === 'danger' 
                                ? 'text-red-600 hover:bg-red-50' 
                                : variant === 'secondary'
                                ? 'text-[var(--color-secondary-text)] hover:bg-[var(--color-secondary-bg)]'
                                : 'text-blue-600 hover:bg-blue-50'
                            }`}
                            title={label}
                          >
                            <IconComponent style={{ width: '16px', height: '16px' }} />
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
                ))}
              </tbody>
          </table>
        </div>
        )}
        
        {/* Pagination */}
        {_showPagination && pagination.totalItems > 0 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            itemsPerPage={pagination.itemsPerPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showPageSizeSelector={true}
            showItemCount={true}
            className="mt-4"
          />
        )}
        

        {/* Delete Confirmation - ConfirmDialog (shared) or inline modal */}
        {pendingDelete && useConfirmDialog ? (
          <ConfirmDialog
            isOpen={!!pendingDelete}
            onClose={() => setPendingDelete(null)}
            onConfirm={async () => {
              const itemToDelete = pendingDelete.item;
              await handleDelete(itemToDelete, true);
            }}
            title={t('messages.confirmDeletePrompt') || 'Are you sure you want to delete?'}
            itemName={pendingDelete.item[nameField] || undefined}
            description={t('messages.deleteWarning') || 'This action cannot be undone.'}
            confirmLabel={t('common.delete')}
            cancelLabel={t('common.cancel')}
            variant="danger"
            showDontAskAgain={true}
            dontAskAgainLabel={t('common.dontAskAgain') || "Don't ask again for this session"}
            onDontAskAgainChange={(checked) => checked && onDeleteDontAskAgain?.()}
          />
        ) : pendingDelete && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setPendingDelete(null);
              }
            }}
          >
            <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <TrashIcon className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">
                      {t('messages.confirmDelete').replace('this item', pendingDelete.item[nameField] || t('messages.item'))}
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-[var(--color-secondary-text)] mb-6">
                  {t('messages.deleteWarning') || 'This action cannot be undone.'}
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setPendingDelete(null)}
                    className="px-4 py-2 text-sm font-medium text-[var(--color-surface-text)] bg-[var(--color-secondary-bg)] rounded-md hover:bg-[var(--color-surface-bg)] transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={() => {
                      const itemToDelete = pendingDelete.item;
                      setPendingDelete(null);
                      handleDelete(itemToDelete, true);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hover Tooltip */}
        {hoveredItem && (
          <div 
            className="fixed z-50 bg-gray-900 text-white p-4 rounded-lg shadow-lg pointer-events-none"
            style={{
              left: `${hoverPosition.x + 10}px`,
              top: `${hoverPosition.y - 10}px`,
              transform: 'translateY(-100%)',
              minWidth: '300px',
              maxWidth: '500px',
              width: 'auto'
            }}
          >
            <div className="text-sm font-semibold mb-2 border-b border-gray-700 pb-2">
              {hoveredItem[entity.nameField] || hoveredItem.first_name ? 
                `${hoveredItem.first_name || ''} ${hoveredItem.last_name || ''}`.trim() : 
                'Details'}
            </div>
            
            <div className="text-sm">
              {/* Show interactions for contacts, description for other entities */}
              {hoveredItem.first_name && hoveredItem.last_name ? (
                <div className="max-h-60 overflow-y-auto">
                  {loadingInteractions ? (
                    <div className="text-gray-400 italic">Loading interactions...</div>
                  ) : hoveredInteractions.length > 0 ? (
                    <div className="space-y-2">
                      {hoveredInteractions.slice(0, 5).map((interaction: any, index: number) => (
                        <div key={interaction.id || index} className="bg-gray-800 p-2 rounded">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-blue-300 uppercase">
                              {interaction.interaction_type || 'Interaction'}
                            </span>
                            {interaction.interaction_date && (
                              <span className="text-xs text-gray-400">
                                {new Date(interaction.interaction_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {interaction.subject && (
                            <div className="text-xs font-medium text-gray-200 mb-1">
                              {interaction.subject}
                            </div>
                          )}
                          {interaction.description && (
                            <div className="text-xs text-gray-300 whitespace-pre-wrap break-words">
                              {interaction.description.length > 150 
                                ? `${interaction.description.substring(0, 150)}...` 
                                : interaction.description}
                            </div>
                          )}
                        </div>
                      ))}
                      {hoveredInteractions.length > 5 && (
                        <div className="text-xs text-gray-400 italic text-center">
                          +{hoveredInteractions.length - 5} more interactions
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-gray-400 italic">No interactions available</div>
                  )}
                </div>
              ) : hoveredItem.description ? (
                <div className="text-gray-100 bg-gray-800 p-3 rounded max-h-60 overflow-y-auto whitespace-pre-wrap break-words">
                  {hoveredItem.description}
                </div>
              ) : (
                <div className="text-gray-400 italic">No details available</div>
              )}
            </div>
          </div>
        )}
      </div>
  );
};